import type {CloseReason} from '../../types';

import {BottomSheet, Button, Radio, TextArea} from '@ds/components';
import {useEffect, useState} from 'react';

type StoreStatusBottomSheetProps = {
  show: boolean;
  isActive: boolean;
  reasons: CloseReason[];
  loading?: boolean;
  onClose: () => void;
  onActivate: () => void;
  onDeactivate: (reasonId: string, closedUntil: string) => void;
};

const CLOSE_TIMES = [
  {
    title: 'تا پایان روز (ساعت ۱۲ شب)',
    subtitle: 'با انتخاب این گزینه، فروشگاه شما تا زمان فعال‌سازی مجدد، غیرفعال خواهد شد.',
    id: 'day_end'
  },
  {title: 'تا پایان هفته (جمعه ۱۲ شب)', id: 'week_end'}
];

const StoreStatusBottomSheet: React.FC<StoreStatusBottomSheetProps> = ({
  show,
  isActive,
  reasons,
  loading = false,
  onClose,
  onActivate,
  onDeactivate
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [closedUntil, setClosedUntil] = useState('day_end');

  useEffect(() => {
    if (show && reasons.length > 0 && !selectedReasonId) {
      setSelectedReasonId(String(reasons[0].id));
    }
  }, [reasons, selectedReasonId, show]);

  return (
    <BottomSheet
      defaultHeader
      title="تغییر وضعیت فروشگاه"
      show={show}
      onClose={onClose}
      contentStyle={{maxHeight: '80%', overflow: 'auto', padding: 0}}
      footer={
        <Button
          fullWidth
          loading={loading}
          disabled={isActive && !selectedReasonId}
          onClick={() =>
            isActive ? onDeactivate(selectedReasonId, closedUntil) : onActivate()
          }
        >
          {isActive ? 'غیرفعال کردن فروشگاه' : 'فعال کردن فروشگاه'}
        </Button>
      }
      footerStyle={{padding: '10px 16px', backgroundColor: 'var(--color-white)'}}
    >
      {isActive ? (
        <div className="flex flex-col gap-2 bg-gray-02">
          <section className="bg-white p-4">
            <h3 className="mb-3 text-body-sm-emphasis text-gray-09">مدت بستن فروشگاه:</h3>
            <Radio.Group
              className="gap-4"
              value={closedUntil}
              onChange={(event) => setClosedUntil(event.target.value)}
            >
              {CLOSE_TIMES.map((time) => (
                <Radio key={time.id} value={time.id}>
                  <span className="flex flex-col">
                    <span>{time.title}</span>
                    {time.subtitle && (
                      <span className="text-caption text-gray-07">{time.subtitle}</span>
                    )}
                  </span>
                </Radio>
              ))}
            </Radio.Group>
          </section>
          <section className="bg-white p-4">
            <h3 className="mb-3 text-body-sm-emphasis text-gray-09">دلیل بستن فروشگاه:</h3>
            <Radio.Group
              className="gap-6"
              value={selectedReasonId}
              onChange={(event) => setSelectedReasonId(event.target.value)}
            >
              {reasons.map((reason) => (
                <Radio key={reason.id} value={String(reason.id)}>
                  {reason.attributes.title}
                </Radio>
              ))}
            </Radio.Group>
          </section>
          <div className="bg-white p-4">
            <TextArea className="w-full" rows={4} placeHolder="توضیحات" />
          </div>
        </div>
      ) : (
        <p className="p-4 text-body-sm text-gray-08">آیا می‌خواهید فروشگاه را فعال کنید؟</p>
      )}
    </BottomSheet>
  );
};

export default StoreStatusBottomSheet;
